import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authenticateWithPasskey, isWebAuthnSupported, isConditionalMediationAvailable } from '../api/webauthn';
import { AuthLayout } from '../components/auth/AuthLayout';
import { Banner, Button, Field, Input, inlineLinkClass } from '../components/ui';
import { titleTelegramWidgetIframe } from '../utils/telegramWidgetA11y';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, telegramLogin, loadUser } = useAuth();
  const navigate = useNavigate();
  const passkeySupported = isWebAuthnSupported();
  const conditionalAbortRef = useRef<AbortController | null>(null);

  // Auto-trigger passkey login via conditional mediation (autofill UI)
  useEffect(() => {
    let cancelled = false;

    async function tryConditionalLogin() {
      if (!await isConditionalMediationAvailable()) return;

      try {
        const abortController = new AbortController();
        conditionalAbortRef.current = abortController;

        // Get options from server
        const { startAuthentication } = await import('@simplewebauthn/browser');
        const { api } = await import('../api/client');
        const { data: options } = await api.post('/user/webauthn/authenticate-options');

        // Start conditional (autofill) authentication
        const credential = await startAuthentication({
          optionsJSON: options,
          useBrowserAutofill: true,
        });

        if (cancelled) return;

        // Verify with server
        await api.post('/user/webauthn/authenticate-verify', { credential });
        await loadUser();
        navigate('/dashboard');
      } catch (err: any) {
        // NotAllowedError = user dismissed, AbortError = navigated away — both are fine
        if (cancelled || err.name === 'NotAllowedError' || err.name === 'AbortError') return;
        console.debug('Conditional mediation failed:', err.message);
      }
    }

    tryConditionalLogin();

    return () => {
      cancelled = true;
      conditionalAbortRef.current?.abort();
    };
  }, [loadUser, navigate]);

  const handlePasskeyLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      await authenticateWithPasskey();
      await loadUser();
      navigate('/dashboard');
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey-Anmeldung abgebrochen.');
      } else {
        setError(err.response?.data?.error || err.message || 'Passkey-Anmeldung fehlgeschlagen.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Telegram Login Handler
  React.useEffect(() => {
    // Define the global callback
    if (!(window as any).WohnungsAgent) {
      (window as any).WohnungsAgent = {};
    }

    (window as any).WohnungsAgent.onTelegramAuth = async (user: any) => {
      console.log('Telegram auth callback triggered with user:', user);
      setError('');
      setIsLoading(true);
      try {
        console.log('Calling telegramLogin...');
        await telegramLogin(user);
        console.log('telegramLogin successful, navigating to dashboard...');
        navigate('/dashboard');
      } catch (err: any) {
        console.error('Telegram login error:', err);
        setError(err.message || 'Telegram-Login fehlgeschlagen.');
        setIsLoading(false);
      }
    };

    console.log('Global callback defined at window.WohnungsAgent.onTelegramAuth');

    // Inject Script
    const script = document.createElement('script');
    script.src = "https://telegram.org/js/telegram-widget.js?7";
    const botName = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'WohnungsAgentBot';
    console.log('Initializing Telegram Widget for bot:', botName);
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'window.WohnungsAgent.onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;

    const container = document.getElementById('telegram-login-container');
    let disconnectTitleWatcher: (() => void) | undefined;
    if (container) {
        console.log('Injecting Telegram Widget script...');
        container.appendChild(script);
        // The widget injects its iframe without a title (axe: frame-title);
        // watch for it and set one once it appears.
        disconnectTitleWatcher = titleTelegramWidgetIframe(container, 'Mit Telegram anmelden');
    } else {
        console.error('Telegram login container not found!');
    }

    return () => {
        // Cleanup? Telegram widget might leave artifacts but removing script is good practice
        /*
           Note: The widget creates an iframe. Removing the script element doesn't remove the iframe
           if it's already rendered, but clearing container.innerHTML above handles re-mounts.
        */
        console.log('Cleaning up Telegram Widget setup...');
        disconnectTitleWatcher?.();
        if ((window as any).WohnungsAgent) {
            delete (window as any).WohnungsAgent.onTelegramAuth;
        }
    };
  }, [telegramLogin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Anmeldung fehlgeschlagen. Bitte versuch es noch einmal.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Anmelden"
      description="Willkommen zurück."
      footer={
        <p>
          Noch kein Konto?{' '}
          <Link to="/register" className={`${inlineLinkClass} font-semibold`}>
            Registrieren
          </Link>
        </p>
      }
    >
      <div className="mb-6 flex justify-center">
        <div id="telegram-login-container" role="group" aria-label="Mit Telegram anmelden" />
      </div>

      <div className="mb-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-line" />
        <span className="text-sm text-muted">oder mit E-Mail</span>
        <div className="h-px flex-1 bg-line" />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <Banner tone="danger">{error}</Banner>
        )}

        <Field label="E-Mail-Adresse" htmlFor="email">
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.de"
            required
            autoFocus
            autoComplete="username webauthn"
            disabled={isLoading}
          />
        </Field>

        <Field label="Passwort" htmlFor="password">
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
            disabled={isLoading}
            minLength={8}
          />
        </Field>

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm font-medium text-brand hover:underline">
            Passwort vergessen?
          </Link>
        </div>

        <div className="flex flex-col gap-3">
          <Button type="submit" fullWidth loading={isLoading}>
            Anmelden
          </Button>
          {passkeySupported && (
            <Button
              type="button"
              variant="secondary"
              fullWidth
              leftIcon={<KeyRound className="h-4 w-4" aria-hidden="true" />}
              onClick={handlePasskeyLogin}
              disabled={isLoading}
            >
              Mit Passkey anmelden
            </Button>
          )}
        </div>
      </form>
    </AuthLayout>
  );
};

export default Login;
